// src/components/admin/ProductManagement.js
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Heading,
  SimpleGrid,
  Text,
  Checkbox,
  Wrap,
  Badge,
  Image,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Stack,
  HStack,
  IconButton,
} from '@chakra-ui/react';
import { SearchIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const colorOptions = [
  { value: 'pink', label: 'ピンク系' },
  { value: 'red', label: 'レッド系' },
  { value: 'purple', label: 'パープル系' },
  { value: 'blue', label: 'ブルー系' },
  { value: 'green', label: 'グリーン系' },
  { value: 'brown', label: 'ブラウン系' },
  { value: 'beige', label: 'ベージュ系' },
  { value: 'black', label: 'ブラック系' },
  { value: 'metallic', label: 'シルバー・ゴールド系' }
];

const featureOptions = [
  { value: 'lightweight', label: '軽量' },
  { value: 'large-capacity', label: '大容量' },
  { value: 'durable', label: '頑丈💪' },
  { value: 'small-child', label: '小柄なお子様におすすめ' },
  { value: 'tablet-pocket', label: 'タブレットポケット' },
  { value: 'clarino', label: 'クラリーノ(人工皮革)' },
  { value: 'cordovan', label: 'コードバン(馬革)' }
];

const initialFormData = {
  code: '',
  name: '',
  gender: '',
  price: '',
  priceRange: '',
  colors: [],
  features: [],
  imageUrl: '',
  purchaseUrl: '',
  sortPriority: 0  // 追加
};

const ProductManagement = () => {
  const [randoserus, setRandoserus] = useState([]);
  const [formData, setFormData] = useState(initialFormData);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const cancelRef = useRef();
  const toast = useToast();

  const fetchRandoserus = useCallback(async () => {
    try {
      console.log('Fetching from:', `${API_URL}/api/admin/randoseru`);
      const response = await axios.get(`${API_URL}/api/admin/randoseru`, {
        params: {
          sort: 'createdAt',
          order: 'desc'
        }
      });
      console.log('API Response:', response.data);

      if (response.data && response.data.randoserus) {
        console.log('Setting randoserus:', response.data.randoserus);
        setRandoserus(response.data.randoserus);
      } else {
        console.error('Unexpected API response format:', response.data);
        setRandoserus([]);
      }
    } catch (error) {
      console.error('Error fetching randoserus:', error.response || error);
      toast({
        title: 'ランドセルの取得に失敗しました',
        description: error.response?.data?.message || 'サーバーエラーが発生しました',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setRandoserus([]);
    }
  }, [toast]);

  // データ取得のuseEffect（既存）
    useEffect(() => {
      fetchRandoserus();
    }, [fetchRandoserus]);

    // 検索時にページを1に戻すuseEffect（新規追加）
    useEffect(() => {
      setCurrentPage(1);
    }, [searchQuery]);

  // フィルタリングとソートされたデータの計算
  const filteredAndSortedRandoserus = useMemo(() => {
  // 検索フィルター
  let filtered = randoserus.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ソート
  filtered = [...filtered].sort((a, b) => {
    if (sortField === 'createdAt') {
      return sortOrder === 'desc' 
        ? new Date(b.createdAt) - new Date(a.createdAt)
        : new Date(a.createdAt) - new Date(b.createdAt);
    }
    if (sortField === 'price') {
      return sortOrder === 'desc' ? b.price - a.price : a.price - b.price;
    }
    if (sortField === 'sortPriority') {
      const priorityA = a.sortPriority || 0;
      const priorityB = b.sortPriority || 0;
      return sortOrder === 'desc' ? priorityB - priorityA : priorityA - priorityB;
    }
    if (sortField === 'code' || sortField === 'name') {
      return sortOrder === 'desc'
        ? b[sortField].localeCompare(a[sortField])
        : a[sortField].localeCompare(b[sortField]);
    }
    return 0;
  });

  return filtered;
}, [randoserus, searchQuery, sortField, sortOrder]);

  // ページネーションの計算
  const totalPages = Math.ceil(filteredAndSortedRandoserus.length / itemsPerPage);
  const currentItems = filteredAndSortedRandoserus.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updates = {
        ...prev,
        [name]: value
      };
  
      if (name === 'price') {
        const price = Number(value);
        if (price >= 60000 && price <= 69999) {
          updates.priceRange = '60k';
        } else if (price >= 70000 && price <= 79999) {
          updates.priceRange = '70k';
        } else {
          updates.priceRange = 'all';
        }
      }
  
      return updates;
    });
  };

  const handleColorChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      const newColors = checked
        ? [...(prev.colors || []), value]
        : (prev.colors || []).filter(c => c !== value);
      return {
        ...prev,
        colors: newColors
      };
    });
  };

  const handleFeatureChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      const newFeatures = checked
        ? [...(prev.features || []), value]
        : (prev.features || []).filter(f => f !== value);
      return {
        ...prev,
        features: newFeatures
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    try {
      const price = Number(formData.price) || 0;
      console.log('Processing price:', price);
  
      let priceRange = formData.priceRange;
      if (price >= 60000 && price <= 69999) {
        priceRange = '60k';
      } else if (price >= 70000 && price <= 79999) {
        priceRange = '70k';
      } else {
        priceRange = 'all';
      }
  
      const dataToSend = {
        ...formData,
        code: formData.code,
        colors: Array.from(formData.colors || []),
        features: Array.from(formData.features || []),
        price: price,
        priceRange: priceRange,
        sortPriority: Number(formData.sortPriority) || 0
      };
  
      console.log('送信するデータ:', JSON.stringify(dataToSend, null, 2));
  
      if (isEditing) {
        const response = await axios.put(
          `${API_URL}/api/admin/randoseru/${editingId}`, 
          dataToSend
        );
        console.log('更新レスポンス:', response.data);
        toast({
          title: 'ランドセルが更新されました',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setIsEditing(false);
        setEditingId(null);
      } else {
        const response = await axios.post(
          `${API_URL}/api/admin/randoseru`,
          dataToSend
        );
        console.log('作成レスポンス:', response.data);
        toast({
          title: 'ランドセルが追加されました',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      resetForm();
      fetchRandoserus();
    } catch (error) {
      console.error('API エラー詳細:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      toast({
        title: 'エラーが発生しました',
        description: error.response?.data?.message || 'サーバーエラーが発生しました',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };
  
  const handleEdit = (randoseru) => {
    setFormData({
      code: randoseru.code,
      name: randoseru.name,
      gender: randoseru.gender,
      price: randoseru.price,
      priceRange: randoseru.priceRange,
      colors: randoseru.colors || [],
      features: randoseru.features || [],
      imageUrl: randoseru.imageUrl,
      purchaseUrl: randoseru.purchaseUrl,
      sortPriority: randoseru.sortPriority || 0
    });
    setIsEditing(true);
    setEditingId(randoseru._id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleDuplicate = (randoseru) => {
    const duplicatedData = {
      ...randoseru,
      code: `${randoseru.code}_copy`,
      name: `${randoseru.name}（複製）`,
      sortPriority: randoseru.sortPriority || 0,
      _id: undefined
    };
    setFormData(duplicatedData);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const openDeleteDialog = (id) => {
    setDeletingId(id);
    setIsDeleteDialogOpen(true);
  };
  
  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/admin/randoseru/${deletingId}`);
      toast({
        title: 'ランドセルが削除されました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setIsDeleteDialogOpen(false);
      fetchRandoserus();
    } catch (error) {
      toast({
        title: 'エラーが発生しました',
        description: error.response?.data?.message || 'サーバーエラーが発生しました',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setIsEditing(false);
    setEditingId(null);
  };

  return (
    <Box>
      <Heading as="h2" size="lg" mb={6}>商品管理</Heading>
      
      <Box bg="white" p={6} borderRadius="md" boxShadow="md">
        <form onSubmit={handleSubmit}>
          <SimpleGrid columns={[1, 2]} spacing={6}>
            <FormControl>
              <FormLabel>品番</FormLabel>
              <Input 
                name="code" 
                value={formData.code} 
                onChange={handleInputChange} 
              />
            </FormControl>

            <FormControl>
              <FormLabel>商品名</FormLabel>
              <Input 
                name="name" 
                value={formData.name} 
                onChange={handleInputChange} 
              />
            </FormControl>

            <FormControl>
              <FormLabel>性別</FormLabel>
              <Select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                placeholder="選択してください"
              >
                <option value="boy">男の子</option>
                <option value="girl">女の子</option>
                <option value="unisex">男女共用</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>実売価格</FormLabel>
              <Input
                name="price"
                type="number"
                value={formData.price}
                onChange={handleInputChange}
              />
            </FormControl>

            <FormControl>
              <FormLabel>価格帯</FormLabel>
              <Select
                name="priceRange"
                value={formData.priceRange}
                onChange={handleInputChange}
                placeholder="選択してください"
              >
                <option value="60k">6万台のランドセル</option>
                <option value="70k">7万台のランドセル</option>
                <option value="all">特になし</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>表示優先順位</FormLabel>
              <Input
                name="sortPriority"
                type="number"
                value={formData.sortPriority || 0}
                onChange={handleInputChange}
                placeholder="0が標準（数値が大きいほど優先度が高い）"
              />
              <Text fontSize="sm" color="gray.600" mt={1}>
                マッチ度が同じ場合の表示順位（0が標準）
              </Text>
            </FormControl>
          </SimpleGrid>
  
          <Box mt={6}>
            <FormControl>
              <FormLabel>カラー</FormLabel>
              <SimpleGrid columns={[2, 3, 4]} spacing={4}>
                {colorOptions.map(color => (
                  <Checkbox
                    key={color.value}
                    value={color.value}
                    isChecked={formData.colors?.includes(color.value)}
                    onChange={handleColorChange}
                  >
                    {color.label}
                  </Checkbox>
                ))}
              </SimpleGrid>
            </FormControl>
          </Box>

          <Box mt={6}>
            <FormControl>
              <FormLabel>特徴</FormLabel>
              <SimpleGrid columns={[2, 3]} spacing={4}>
                {featureOptions.map(feature => (
                  <Checkbox
                    key={feature.value}
                    value={feature.value}
                    isChecked={formData.features?.includes(feature.value)}
                    onChange={handleFeatureChange}
                  >
                    {feature.label}
                  </Checkbox>
                ))}
              </SimpleGrid>
            </FormControl>
          </Box>

          <SimpleGrid columns={[1, 2]} spacing={6} mt={6}>
            <FormControl>
              <FormLabel>商品画像URL</FormLabel>
              <Input
                name="imageUrl"
                value={formData.imageUrl}
                onChange={handleInputChange}
              />
            </FormControl>

            <FormControl>
              <FormLabel>購入ページURL</FormLabel>
              <Input
                name="purchaseUrl"
                value={formData.purchaseUrl}
                onChange={handleInputChange}
              />
            </FormControl>
          </SimpleGrid>

          <Button 
            type="submit" 
            colorScheme="blue" 
            mt={6} 
            size="lg" 
            width="full"
          >
            {isEditing ? 'ランドセルを更新' : 'ランドセルを追加'}
          </Button>
        </form>
      </Box>

      <Box mt={10}>
        <Heading as="h2" size="lg" mb={4}>ランドセル一覧</Heading>
        
        <Stack spacing={4} mb={4}>
          <HStack spacing={4}>
            <InputGroup maxW="300px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="商品名や品番で検索..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>

            <Select
              maxW="200px"
              value={sortField}
              onChange={(e) => setSortField(e.target.value)}
            >
              <option value="createdAt">登録日時</option>
              <option value="code">品番</option>
              <option value="name">商品名</option>
              <option value="price">価格</option>
              <option value="sortPriority">優先順位</option>
            </Select>

            <Button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            >
              {sortOrder === 'asc' ? '昇順' : '降順'}
            </Button>
          </HStack>

          <Text fontSize="sm" color="gray.600">
            全{filteredAndSortedRandoserus.length}件中 
            {(currentPage - 1) * itemsPerPage + 1}～
            {Math.min(currentPage * itemsPerPage, filteredAndSortedRandoserus.length)}件を表示
          </Text>
        </Stack>

        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>品番</Th>
              <Th>商品画像</Th>
              <Th>商品名</Th>
              <Th>性別</Th>
              <Th>価格帯</Th>
              <Th>カラー</Th>
              <Th>特徴</Th>
              <Th>優先順位</Th>
              <Th>操作</Th>
            </Tr>
          </Thead>

          <Tbody>
            {currentItems.length > 0 ? (
              currentItems.map((randoseru) => (
                <Tr key={randoseru._id}>
                  <Td>{randoseru.code}</Td>
                  <Td>
                    <Image
                      src={randoseru.imageUrl}
                      alt={randoseru.name}
                      maxH="100px"
                      objectFit="contain"
                      mx="auto"
                    />
                  </Td>
                  <Td>{randoseru.name}</Td>
                  <Td>{randoseru.gender === 'boy' ? '男の子' : 
                      randoseru.gender === 'girl' ? '女の子' : 
                      '男女共用'}</Td>
                  <Td>{`${randoseru.price.toLocaleString()}円 (${
                    randoseru.priceRange === '60k' ? '6万台' :
                    randoseru.priceRange === '70k' ? '7万台' : 
                    '指定なし'
                  })`}</Td>
                  <Td>
                    <Wrap spacing={1}>
                      {randoseru.colors?.map(color => (
                        <Badge key={color} colorScheme="blue" variant="subtle">
                          {colorOptions.find(opt => opt.value === color)?.label || color}
                        </Badge>
                      ))}
                    </Wrap>
                  </Td>
                  <Td>
                    <Wrap spacing={1}>
                      {randoseru.features?.map(feature => (
                        <Badge key={feature} colorScheme="green" variant="subtle">
                          {featureOptions.find(opt => opt.value === feature)?.label || feature}
                        </Badge>
                      ))}
                    </Wrap>
                  </Td>
                  <Td>{randoseru.sortPriority || 0}</Td>
                  <Td>
                    <Button 
                      onClick={() => handleEdit(randoseru)} 
                      colorScheme="blue" 
                      size="sm" 
                      mr={2}
                    >
                      編集
                    </Button>
                    <Button
                      onClick={() => handleDuplicate(randoseru)}
                      colorScheme="green"
                      size="sm"
                      mr={2}
                    >
                      複製
                    </Button>
                    <Button 
                      onClick={() => openDeleteDialog(randoseru._id)} 
                      colorScheme="red" 
                      size="sm"
                    >
                      削除
                    </Button>
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={9}>
                  <Text textAlign="center">該当するランドセルがありません</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>

        <HStack justify="center" mt={4} spacing={4}>
          <IconButton
            icon={<ChevronLeftIcon />}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            isDisabled={currentPage === 1}
            aria-label="前のページ"
          />
          <Text>
            {currentPage} / {totalPages} ページ
          </Text>
          <IconButton
            icon={<ChevronRightIcon />}
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            isDisabled={currentPage === totalPages}
            aria-label="次のページ"
          />
        </HStack>
      </Box>

      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              ランドセルの削除
            </AlertDialogHeader>

            <AlertDialogBody>
              本当にこのランドセルを削除しますか？
              この操作は取り消すことができません。
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button 
                ref={cancelRef} 
                onClick={() => setIsDeleteDialogOpen(false)}
              >
                キャンセル
              </Button>
              <Button 
                colorScheme="red" 
                onClick={handleDelete} 
                ml={3}
              >
                削除する
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );


};

export default ProductManagement;